import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, ArrowLeft, Sparkles, CheckCircle2, Loader2, Leaf } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const steps = [
  { key: 'age', title: 'What’s your age group?', subtitle: 'Nutrition needs shift with each decade.', options: ['Under 25', '25-34', '35-44', '45-54', '55+'] },
  { key: 'gender', title: 'How do you identify?', subtitle: 'Hormones matter — we tailor accordingly.', options: ['Female', 'Male', 'Non-binary', 'Prefer not to say'] },
  { key: 'goal', title: 'What’s your main goal?', subtitle: 'Pick the one that resonates most.', options: ['Sustainable weight loss', 'Weight gain / muscle', 'Manage PCOS / hormones', 'Manage diabetes / BP', 'Pre / post pregnancy', 'Pre-wedding glow', 'Overall energy & maintenance'] },
  { key: 'activity', title: 'How active are you?', subtitle: 'Be honest — we won’t judge.', options: ['Barely move', '1-2 workouts/week', '3-5 workouts/week', 'Daily athlete'] },
];

const conditionOpts = ['PCOS/PCOD', 'Diabetes', 'Hypertension', 'Thyroid', 'Anaemia', 'IBS/Gut issues', 'None'];

const Quiz = () => {
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [ans, setAns] = useState({ age: '', gender: '', goal: '', activity: '', conditions: [], notes: '' });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const total = steps.length + 2;
  const progress = ((step + 1) / total) * 100;

  const pick = (val) => {
    const key = steps[step].key;
    setAns({ ...ans, [key]: val });
    setTimeout(() => setStep(step + 1), 150);
  };

  const toggleCondition = (c) => {
    setAns((a) => ({ ...a, conditions: a.conditions.includes(c) ? a.conditions.filter((x) => x !== c) : [...a.conditions, c] }));
  };

  const submit = async () => {
    setLoading(true);
    try {
      const r = await axios.post(`${API}/quiz/recommend`, ans);
      setResult(r.data.recommendation);
    } catch (e) {
      setResult({ program: 'Basic Diet', reason: 'Let\u2019s start simple. Your dietitian will fine-tune everything on the call.', tips: [] });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#faf7f1] pt-28 pb-20 px-6">
      <div className="max-w-2xl mx-auto">
        <button onClick={() => nav('/')} className="text-sm text-[#7a8471] mb-6 inline-flex items-center gap-2 hover:text-[#3a5a2c]">
          <ArrowLeft className="w-4 h-4" /> Back home
        </button>

        {!result && (
          <div className="bg-white rounded-3xl shadow-lg p-8 md:p-12">
            <div className="flex items-center gap-2 mb-6">
              <Sparkles className="w-4 h-4 text-[#c9743a]" />
              <span className="text-xs tracking-[0.2em] uppercase text-[#c9743a]">AI Diet Quiz</span>
            </div>

            <div className="h-1.5 rounded-full bg-neutral-100 mb-8 overflow-hidden">
              <div className="h-full bg-[#3a5a2c] transition-all duration-500" style={{ width: `${progress}%` }} />
            </div>

            {step < steps.length && (
              <div>
                <h2 className="font-serif text-3xl md:text-4xl text-[#1a2b17] mb-2">{steps[step].title}</h2>
                <p className="text-[#4a5544] mb-8">{steps[step].subtitle}</p>
                <div className="grid gap-3">
                  {steps[step].options.map((o) => (
                    <button key={o} onClick={() => pick(o)} className={`text-left px-5 py-4 rounded-2xl border-2 transition-all ${ans[steps[step].key] === o ? 'border-[#3a5a2c] bg-[#e8f2e2]' : 'border-neutral-200 hover:border-[#3a5a2c] hover:bg-[#faf7f1]'}`}>
                      {o}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {step === steps.length && (
              <div>
                <h2 className="font-serif text-3xl md:text-4xl text-[#1a2b17] mb-2">Any health conditions?</h2>
                <p className="text-[#4a5544] mb-8">Select all that apply.</p>
                <div className="grid grid-cols-2 gap-3">
                  {conditionOpts.map((c) => (
                    <label key={c} className={`flex items-center gap-3 px-4 py-3 rounded-2xl border-2 cursor-pointer transition ${ans.conditions.includes(c) ? 'border-[#3a5a2c] bg-[#e8f2e2]' : 'border-neutral-200 hover:border-[#3a5a2c]'}`}>
                      <Checkbox checked={ans.conditions.includes(c)} onCheckedChange={() => toggleCondition(c)} />
                      <span className="text-sm text-[#1a2b17]">{c}</span>
                    </label>
                  ))}
                </div>
                <div className="flex justify-between mt-8">
                  <Button variant="outline" onClick={() => setStep(step - 1)} className="rounded-full">Back</Button>
                  <Button onClick={() => setStep(step + 1)} className="rounded-full bg-[#3a5a2c] hover:bg-[#2d4622] text-white">Continue <ArrowRight className="w-4 h-4 ml-2" /></Button>
                </div>
              </div>
            )}

            {step === steps.length + 1 && (
              <div>
                <h2 className="font-serif text-3xl md:text-4xl text-[#1a2b17] mb-2">Anything else we should know?</h2>
                <p className="text-[#4a5544] mb-6">Cravings, cuisine, allergies, sleep, stress — spill it all.</p>
                <Textarea value={ans.notes} onChange={(e) => setAns({ ...ans, notes: e.target.value })} className="rounded-2xl min-h-[140px] border-neutral-200" placeholder="I love South Indian food, hate mornings, and can’t give up coffee..." />
                <div className="flex justify-between mt-8">
                  <Button variant="outline" onClick={() => setStep(step - 1)} className="rounded-full">Back</Button>
                  <Button onClick={submit} disabled={loading} className="rounded-full bg-[#3a5a2c] hover:bg-[#2d4622] text-white">
                    {loading ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Analysing…</>) : (<>Get my program <Sparkles className="w-4 h-4 ml-2" /></>)}
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        {result && (
          <div className="bg-white rounded-3xl shadow-xl p-8 md:p-12">
            <div className="w-16 h-16 rounded-full bg-[#e8f2e2] flex items-center justify-center mb-6">
              <Leaf className="w-7 h-7 text-[#3a5a2c]" />
            </div>
            <div className="text-xs tracking-[0.2em] uppercase text-[#c9743a] mb-2">Your match</div>
            <h2 className="font-serif text-4xl md:text-5xl text-[#1a2b17]">{result.program}</h2>
            <p className="mt-5 text-[#4a5544] leading-relaxed text-lg">{result.reason}</p>

            {result.tips && result.tips.length > 0 && (
              <div className="mt-8">
                <div className="text-sm tracking-[0.2em] uppercase text-[#c9743a] mb-3">Start with these</div>
                <ul className="space-y-3">
                  {result.tips.map((t, i) => (
                    <li key={i} className="flex items-start gap-3 text-[#1a2b17]">
                      <CheckCircle2 className="w-5 h-5 text-[#3a5a2c] flex-shrink-0 mt-0.5" />
                      <span>{t}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="mt-10 flex flex-col sm:flex-row gap-3">
              <Button onClick={() => nav('/?book=1')} className="h-13 px-8 rounded-full bg-[#3a5a2c] hover:bg-[#2d4622] text-white py-4">Book free consultation <ArrowRight className="w-4 h-4 ml-2" /></Button>
              <Button variant="outline" onClick={() => nav('/#services')} className="h-13 px-8 rounded-full py-4">Explore the program</Button>
              <Button variant="ghost" onClick={() => { setResult(null); setStep(0); setAns({ age: '', gender: '', goal: '', activity: '', conditions: [], notes: '' }); }} className="h-13 px-8 rounded-full py-4">Retake quiz</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Quiz;
